import React, { useState, useEffect, useMemo } from 'react';

import { Segmented, Select } from 'antd';

import { checkTenant } from '@libs/NwUtils';

import { useLayoutContext } from '@contexts/LayoutContext';
import { useProjectContext, useProjectContextDispatch, reloadProjectAccountingData } from '@contexts/ProjectContext';
import { useStateValue } from '@contexts/StateProvider';
import { useUserPermission } from '@contexts/UserContext';

import { onGetOrderedEventList } from '@hooks/API/Project/UseProject';

import { Loading } from '@components/Gui/Loading';
import NwDrawer from '@components/Gui/NwDrawer';
import {
    getTransactionContactBillingInfo,
    getTransactionContacts,
} from '@components/Project/ProjectTransactions/Transactions/TransactionCreateFormUtils';
import AgencyFeeForm from '@components/Transactions/TransactionsCreateForms/AgencyFeeForm';
import JobAdvanceForm from '@components/Transactions/TransactionsCreateForms/JobAdvanceForm';
import JobCommAsMothAgForm from '@components/Transactions/TransactionsCreateForms/JobCommAsMothAgForm';
import JobCreditForm from '@components/Transactions/TransactionsCreateForms/JobCreditForm';
import JobExpenseForm from '@components/Transactions/TransactionsCreateForms/JobExpenseForm';
import JobFeeForm from '@components/Transactions/TransactionsCreateForms/JobFeeForm';
import JobRefundForm from '@components/Transactions/TransactionsCreateForms/JobRefundForm';
import ProductForm from '@components/Transactions/TransactionsCreateForms/ProductForm';
import SystemMessage from '@components/Utils/SystemMessage';

import ExpenseFormProject from '../../../Transactions/TransactionsCreateForms/ExpenseFormProject';
import { TransactionDrawerContent } from './ProjectTransactionsUI';

const { Option } = Select;

const AddProjectTransactionDrawer = ({ projectId, transactionGroup, onClose }) => {
    const drawerWidth = '60vw';
    const drawerMinWidth = '980px';
    const [touchForm, setTouchForm] = useState(false);
    const { projectData: currentProject } = useProjectContext();
    const projectDispatch = useProjectContextDispatch();
    const [contacts, setContacts] = useState([]);
    const [clientBillingInfos, setClientBillingInfos] = useState(null);
    const [{ areaContext }] = useStateValue();

    const accessAccounting = useUserPermission('AccessAccounting');

    const onCloseDrawer = () => {
        reloadProjectAccountingData(projectId, projectDispatch);
        onClose();
    };

    // Check if project has events of type 'job' or 'casting' (required for JobFee - Model Fee)
    const hasJobOrCastingEvents = useMemo(() => {
        if (!currentProject?.Events) return false;
        return currentProject.Events.some(
            (event) => event.TypeName?.toLowerCase() === 'job' || event.TypeName?.toLowerCase() === 'casting'
        );
    }, [currentProject]);

    // Calculate default tab based on transaction group and available events
    const defaultTab = useMemo(() => {
        if (transactionGroup === 'Fee' && !hasJobOrCastingEvents) {
            return 2; // Default to Product/Service if no job/casting events
        }
        return 1; // Default to first option
    }, [transactionGroup, hasJobOrCastingEvents]);

    const [selectedTab, setSelectedTab] = useState(defaultTab);

    // Reset selected tab when transaction group or events change
    useEffect(() => {
        setSelectedTab(defaultTab);
    }, [defaultTab]);

    useEffect(() => {
        const fetchBillingInfo = async (contactID) => {
            const billingInfos = await getTransactionContactBillingInfo(contactID);
            setClientBillingInfos(billingInfos);
        };

        if (currentProject) {
            const events = onGetOrderedEventList(currentProject, areaContext, false, false);
            setContacts(getTransactionContacts(currentProject, events));
            fetchBillingInfo(currentProject.CustomerID);
        }
    }, [currentProject]);

    const FamilyCreateForm = () => {
        const { isMobile } = useLayoutContext();

        if (!currentProject || !contacts || !clientBillingInfos) {
            return <Loading />;
        }

        // Unified component for Select/Segmented
        const TabSelector = ({ options, value, onChange, style }) => {
            if (isMobile) {
                return (
                    <Select
                        style={style || { width: '100%', marginBottom: '16px' }}
                        value={value}
                        onChange={onChange}
                        size="large"
                    >
                        {options.map((option) => (
                            <Option key={option.value} value={option.value} disabled={option.disabled}>
                                <div>
                                    <div>{option.text}</div>
                                    {option.additionalText && (
                                        <small style={{ color: '#666', fontSize: '12px' }}>
                                            {option.additionalText}
                                        </small>
                                    )}
                                </div>
                            </Option>
                        ))}
                    </Select>
                );
            } else {
                const segmentedOptions = options.map((option) => ({
                    label: (
                        <div>
                            <h2>{option.text}</h2>
                            {option.additionalText && <small>{option.additionalText}</small>}
                        </div>
                    ),
                    value: option.value,
                    disabled: option.disabled,
                }));

                return <Segmented block value={value} onChange={onChange} options={segmentedOptions} />;
            }
        };

        // Unified options data structure for all transaction groups
        const getTransactionOptions = (group) => {
            switch (group) {
                case 'Fee':
                    return [
                        {
                            text: 'Model Fee',
                            additionalText: !hasJobOrCastingEvents ? '(requires Job or Casting event)' : '',
                            value: 1,
                            disabled: !hasJobOrCastingEvents,
                        },
                        {
                            text: 'Product / Service',
                            additionalText: '',
                            value: 2,
                        },
                        {
                            text: 'Additional Agency Fee',
                            additionalText: '',
                            value: 3,
                        },
                    ];
                case 'Expense':
                    return [
                        {
                            text: 'Agency → Client',
                            additionalText: 'a billable expense paid by the agency',
                            value: 1,
                        },
                        {
                            text: 'Model → Client',
                            additionalText: 'a billable expense paid by the model',
                            value: 2,
                        },
                        {
                            text: 'Agency → Model',
                            additionalText: 'a non billable advance paid by the agency for the model',
                            value: 3,
                        },
                        {
                            text: 'Client → Model',
                            additionalText: 'an expense paid for the model by the client',
                            value: 4,
                        },
                    ];
                case 'Credit':
                    return [
                        {
                            text: 'Job Credit',
                            additionalText: 'a discount, a tax paid by the client',
                            value: 1,
                        },
                        {
                            text: 'Model Advance',
                            additionalText: 'a payroll or an advance paid by the client to the model',
                            value: 2,
                        },
                    ];
                default:
                    return [];
            }
        };

        const options = getTransactionOptions(transactionGroup);

        switch (transactionGroup) {
            case 'Fee':
                return (
                    <TransactionDrawerContent>
                        <TabSelector options={options} value={selectedTab} onChange={setSelectedTab} />
                        {selectedTab === 1 && (
                            <JobFeeForm
                                // currentProject={currentProject}
                                clientBillingInfos={clientBillingInfos}
                                contacts={contacts}
                                onCloseDrawer={onCloseDrawer}
                                width={drawerWidth}
                                minWidth={drawerMinWidth}
                            />
                        )}
                        {selectedTab === 2 && (
                            <ProductForm
                                clientBillingInfos={clientBillingInfos}
                                contacts={contacts}
                                onCloseDrawer={onCloseDrawer}
                                width={drawerWidth}
                                minWidth={drawerMinWidth}
                            />
                        )}
                        {selectedTab === 3 && (
                            <AgencyFeeForm
                                clientBillingInfos={clientBillingInfos}
                                contacts={contacts}
                                onCloseDrawer={onCloseDrawer}
                                width={drawerWidth}
                                minWidth={drawerMinWidth}
                            />
                        )}
                    </TransactionDrawerContent>
                );
            case 'Expense':
                const isEliteBooker = checkTenant('elitemodels', true) && !accessAccounting;
                if (isEliteBooker) {
                    return (
                        <TransactionDrawerContent>
                            <JobRefundForm
                                clientBillingInfos={clientBillingInfos}
                                contacts={contacts}
                                onCloseDrawer={onCloseDrawer}
                                width={drawerWidth}
                                minWidth={drawerMinWidth}
                            />
                        </TransactionDrawerContent>
                    );
                }
                return (
                    <TransactionDrawerContent>
                        <TabSelector options={options} value={selectedTab} onChange={setSelectedTab} />
                        {selectedTab === 1 && (
                            <JobExpenseForm
                                clientBillingInfos={clientBillingInfos}
                                contacts={contacts}
                                onCloseDrawer={onCloseDrawer}
                                width={drawerWidth}
                                minWidth={drawerMinWidth}
                            />
                        )}
                        {selectedTab === 2 && (
                            <JobRefundForm
                                clientBillingInfos={clientBillingInfos}
                                contacts={contacts}
                                onCloseDrawer={onClose}
                                width={drawerWidth}
                                minWidth={drawerMinWidth}
                            />
                        )}
                        {selectedTab === 3 && (
                            <ExpenseFormProject
                                clientBillingInfos={clientBillingInfos}
                                contacts={contacts}
                                onCloseDrawer={onCloseDrawer}
                                width={drawerWidth}
                                minWidth={drawerMinWidth}
                            />
                        )}
                        {selectedTab === 4 && (
                            <JobAdvanceForm
                                clientBillingInfos={clientBillingInfos}
                                contacts={contacts}
                                onCloseDrawer={onClose}
                                width={drawerWidth}
                                minWidth={drawerMinWidth}
                            />
                        )}
                    </TransactionDrawerContent>
                );
            case 'Credit':
                return (
                    <TransactionDrawerContent>
                        <TabSelector options={options} value={selectedTab} onChange={setSelectedTab} />
                        {selectedTab === 1 && (
                            <JobCreditForm
                                clientBillingInfos={clientBillingInfos}
                                contacts={contacts}
                                onCloseDrawer={onCloseDrawer}
                                width={drawerWidth}
                                minWidth={drawerMinWidth}
                            />
                        )}
                        {selectedTab === 2 && (
                            <JobAdvanceForm
                                clientBillingInfos={clientBillingInfos}
                                contacts={contacts}
                                onCloseDrawer={onCloseDrawer}
                                width={drawerWidth}
                                minWidth={drawerMinWidth}
                            />
                        )}
                    </TransactionDrawerContent>
                );
            case 'CommissionAsMotherAgency':
                return (
                    <JobCommAsMothAgForm
                        clientBillingInfos={clientBillingInfos}
                        contacts={contacts}
                        onCloseDrawer={onCloseDrawer}
                        width={drawerWidth}
                        minWidth={drawerMinWidth}
                    />
                );
            default:
                return <SystemMessage type="building" />;
        }
    };

    return (
        <NwDrawer
            title="Add Project Transaction"
            onClose={onClose}
            visible={true}
            width={drawerWidth}
            minWidth={drawerMinWidth}
        >
            <FamilyCreateForm />
        </NwDrawer>
    );
};

export default AddProjectTransactionDrawer;
